import { IconText } from "@codexteam/icons";
import Pickr from "@simonwep/pickr";
import "@simonwep/pickr/dist/themes/nano.min.css";

/**
 * Build styles
 */
require("./index.css").toString();

/**
 * @typedef {object} TextBoxConfig
 * @property {string}  [placeholder]        - placeholder for an empty box
 * @property {boolean} [preserveBlank]      - keep empty boxes when saving
 * @property {string}  [defaultContent]     - HTML used when the block is created empty
 * @property {number}  [defaultLineHeight]  - unitless line-height for new boxes (default 1.4)
 * @property {string}  [muiClassName]       - extra class added to the editable (same as Body block)
 */

/**
 * @typedef {object} TextBoxData
 * @property {string} html                              - sanitized rich text (p, div, span, ul, ol, li, b, i, u, sub, sup, br)
 * @property {"top"|"middle"|"bottom"} verticalAlign    - where the content sits inside the box
 * @property {number} lineHeight                        - unitless line-height for the whole box
 */

const WEIGHTS = [
  [300, "Light"],
  [400, "Regular"],
  [500, "Medium"],
  [600, "Semibold"],
  [700, "Bold"],
  [800, "Extra bold"],
];

const FONT_SIZES = [
  8, 9, 10, 11, 12, 14, 16, 18, 20, 24, 28, 32, 36, 48, 60, 72,
];

const FONT_FAMILIES = [
  ["primary", "Primary", "ce-font-primary"],
  ["secondary", "Secondary", "ce-font-secondary"],
];

const FONT_CLASSES = new Set(FONT_FAMILIES.map(([, , cls]) => cls));

const MIN_SIZE = 6;
const MAX_SIZE = 200;

const V_ALIGN = { top: "flex-start", middle: "center", bottom: "flex-end" };

/* ---------- icons (inline so we don't depend on which icons @codexteam/icons ships) ---------- */
const svg = (body) =>
  `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${body}</svg>`;

const ICONS = {
  bold: `<span class="ce-textbox-toolbar__glyph" style="font-weight:700">B</span>`,
  italic: `<span class="ce-textbox-toolbar__glyph" style="font-style:italic;font-family:serif">I</span>`,
  underline: `<span class="ce-textbox-toolbar__glyph" style="text-decoration:underline">U</span>`,
  sub: `<span class="ce-textbox-toolbar__glyph">x<sub>2</sub></span>`,
  sup: `<span class="ce-textbox-toolbar__glyph">x<sup>2</sup></span>`,
  sizeUp: `<span class="ce-textbox-toolbar__glyph" style="font-weight:600">A<span style="font-size:.7em;vertical-align:super">+</span></span>`,
  sizeDown: `<span class="ce-textbox-toolbar__glyph" style="font-weight:600">A<span style="font-size:.7em;vertical-align:super">−</span></span>`,
  left: svg(`<path d="M2 3h12M2 6.5h8M2 10h12M2 13.5h8"/>`),
  center: svg(`<path d="M2 3h12M4 6.5h8M2 10h12M4 13.5h8"/>`),
  right: svg(`<path d="M2 3h12M6 6.5h8M2 10h12M6 13.5h8"/>`),
  justify: svg(`<path d="M2 3h12M2 6.5h12M2 10h12M2 13.5h12"/>`),
  ul: svg(
    `<circle cx="3" cy="4" r=".7" fill="currentColor"/><circle cx="3" cy="8" r=".7" fill="currentColor"/><circle cx="3" cy="12" r=".7" fill="currentColor"/><path d="M6.5 4h7M6.5 8h7M6.5 12h7"/>`,
  ),
  ol: svg(
    `<text x="1" y="5.5" font-size="5" fill="currentColor" stroke="none">1</text><text x="1" y="9.5" font-size="5" fill="currentColor" stroke="none">2</text><text x="1" y="13.5" font-size="5" fill="currentColor" stroke="none">3</text><path d="M6.5 4h7M6.5 8h7M6.5 12h7"/>`,
  ),
  vTop: svg(
    `<rect x="2" y="2" width="12" height="12" rx="1.5"/><path d="M5 5h6M5 7.5h4"/>`,
  ),
  vMiddle: svg(
    `<rect x="2" y="2" width="12" height="12" rx="1.5"/><path d="M5 6.5h6M5 9h4"/>`,
  ),
  vBottom: svg(
    `<rect x="2" y="2" width="12" height="12" rx="1.5"/><path d="M5 8.5h6M5 11h4"/>`,
  ),
};

/* ---------- helpers ---------- */
const unwrap = (el) => {
  const parent = el.parentNode;
  while (el.firstChild) parent.insertBefore(el.firstChild, el);
  parent.removeChild(el);
};

const isList = (n) =>
  !!n && n.nodeType === 1 && (n.tagName === "UL" || n.tagName === "OL");

const rgbToHex = (rgb) => {
  const m = String(rgb).match(/\d+/g);
  if (!m || m.length < 3) return null;
  return (
    "#" +
    m
      .slice(0, 3)
      .map((n) => Number(n).toString(16).padStart(2, "0"))
      .join("")
  );
};

const escapeHtml = (s) =>
  s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

/* Paste cleaning: keep only the tags/styles this block can produce */
const KEEP_TAGS = new Set([
  "p",
  "div",
  "span",
  "br",
  "b",
  "strong",
  "i",
  "em",
  "u",
  "sub",
  "sup",
  "ul",
  "ol",
  "li",
]);
const KEEP_STYLES = [
  "color",
  "font-weight",
  "font-style",
  "text-decoration",
  "text-align",
  "font-size",
];
const BLOCKISH = new Set([
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "blockquote",
  "pre",
]);
const SKIP_TAGS = new Set(["script", "style", "meta", "title", "head", "link"]);

function cleanNode(node) {
  if (node.nodeType === Node.TEXT_NODE) return escapeHtml(node.textContent);
  if (node.nodeType !== Node.ELEMENT_NODE) return "";

  let tag = node.tagName.toLowerCase();
  if (SKIP_TAGS.has(tag)) return "";
  if (tag === "br") return "<br>";

  const inner = Array.from(node.childNodes).map(cleanNode).join("");
  const st = node.style;

  // Google Docs wraps its whole clipboard payload in <b style="font-weight:normal">
  if (
    (tag === "b" || tag === "strong") &&
    st &&
    /^(normal|400)$/.test(st.fontWeight)
  ) {
    tag = "span";
  }
  if (BLOCKISH.has(tag)) tag = "p";
  if (!KEEP_TAGS.has(tag)) return inner;

  const decls = st
    ? KEEP_STYLES.map((p) => [p, st.getPropertyValue(p).trim()]).filter(
        ([, v]) => v && !["normal", "none", "initial", "inherit"].includes(v),
      )
    : [];
  const style = decls.length
    ? ` style="${decls.map(([p, v]) => `${p}: ${v.replace(/"/g, "'")}`).join("; ")}"`
    : "";

  const cls = Array.from(node.classList || []).filter((c) =>
    FONT_CLASSES.has(c),
  );
  const classAttr = cls.length ? ` class="${cls.join(" ")}"` : "";

  return `<${tag}${classAttr}${style}>${inner}</${tag}>`;
}

const cleanHtml = (html) => {
  const doc = new DOMParser().parseFromString(html, "text/html");
  return Array.from(doc.body.childNodes).map(cleanNode).join("");
};

/**
 * PowerPoint-style text box: rich text + lists in one block.
 * - Floating toolbar while the block is active
 * - Block settings (cog menu): vertical alignment of contents + line-height
 */
export default class RichTextBox {
  static get DEFAULT_PLACEHOLDER() {
    return "Type something…";
  }

  static get toolbox() {
    return { icon: IconText, title: "Text box" };
  }

  static get isReadOnlySupported() {
    return true;
  }

  /**
   * Editor.js reads `enableLineBreaks` (not `isLineBreaksEnabled`) to decide
   * whether Enter creates a new block. We need Enter to make new paragraphs / list items.
   */
  static get enableLineBreaks() {
    return true;
  }

  static get sanitize() {
    const withStyle = { style: true };
    return {
      html: {
        p: withStyle,
        div: withStyle,
        span: withStyle,
        ul: withStyle,
        ol: withStyle,
        li: withStyle,
        br: true,
        b: true,
        strong: true,
        i: true,
        em: true,
        u: true,
        sub: true,
        sup: true,
        span: { style: true, class: true },
      },
    };
  }

  constructor({ data, config, api, readOnly, block }) {
    this.api = api;
    this.config = config || {};
    this.readOnly = readOnly;
    this.block = block;

    this._placeholder =
      this.config.placeholder || RichTextBox.DEFAULT_PLACEHOLDER;
    this._preserveBlank = this.config.preserveBlank ?? false;

    const d = data || {};
    this._data = {
      html: d.html ?? "",
      verticalAlign: V_ALIGN[d.verticalAlign] ? d.verticalAlign : "top",
      lineHeight: Number(d.lineHeight) || this.config.defaultLineHeight || 1.4,
    };

    this._savedRange = null;
    this._toolbar = null;
    this._toolbarOpen = false;
    this._buttons = {};

    this._onSelectionChange = this._onSelectionChange.bind(this);
    this._onDocPointerDown = this._onDocPointerDown.bind(this);
    this._onDocFocusIn = this._onDocFocusIn.bind(this);

    this._element = this._drawView();
  }

  /* ============================================================
   * Block view
   * ============================================================ */

  _drawView() {
    const wrapper = document.createElement("div");
    wrapper.classList.add("ce-textbox");
    // _applyLayout() reads this._element, which the constructor only assigns after
    // _drawView() returns, so set it here first.
    this._element = wrapper;

    const editable = document.createElement("div");
    editable.classList.add("ce-textbox__editable");
    if (this.config.muiClassName)
      editable.classList.add(this.config.muiClassName);
    editable.contentEditable = this.readOnly ? "false" : "true";
    editable.dataset.placeholder = this.api.i18n.t(this._placeholder);
    editable.innerHTML = this._data.html || this.config.defaultContent || "";
    this._editable = editable;
    this._normalizeLists();
    wrapper.appendChild(editable);

    this._applyLayout();
    this._toggleEmpty();

    if (!this.readOnly) {
      wrapper.addEventListener("focusin", () => {
        if (this._toolbarOpen) return;
        document.execCommand("defaultParagraphSeparator", false, "p");
        this._showToolbar();
      });
      editable.addEventListener("input", () => this._toggleEmpty());
      editable.addEventListener("keydown", (e) => this._onKeyDown(e));
      editable.addEventListener("paste", (e) => this._onPaste(e));

      // Clicking empty space in the box (e.g. below bottom-aligned text) focuses the text.
      wrapper.addEventListener("mousedown", (e) => {
        if (e.target === wrapper) {
          e.preventDefault();
          this._focusEnd();
        }
      });
    }

    return wrapper;
  }

  render() {
    return this._element;
  }

  _applyLayout() {
    this._element.style.justifyContent = V_ALIGN[this._data.verticalAlign];
    this._editable.style.lineHeight = String(this._data.lineHeight);
  }

  _isEmpty() {
    return (
      this._editable.textContent.trim() === "" &&
      !this._editable.querySelector("ul, ol")
    );
  }

  _toggleEmpty() {
    this._editable.classList.toggle("is-empty", this._isEmpty());
  }

  _focusEnd() {
    this._editable.focus();
    const sel = window.getSelection();
    const range = document.createRange();
    range.selectNodeContents(this._editable);
    range.collapse(false);
    sel.removeAllRanges();
    sel.addRange(range);
  }

  /* ============================================================
   * Editor.js lifecycle
   * ============================================================ */

  save() {
    return {
      html: this._isEmpty() ? "" : this._editable.innerHTML,
      verticalAlign: this._data.verticalAlign,
      lineHeight: this._data.lineHeight,
    };
  }

  validate(saved) {
    if (this._preserveBlank) return true;
    const tmp = document.createElement("div");
    tmp.innerHTML = saved.html || "";
    return tmp.textContent.trim() !== "";
  }

  destroy() {
    this._hideToolbar();
    if (this._pickr) {
      this._pickr.destroyAndRemove();
      this._pickr = null;
    }
    if (this._toolbar) this._toolbar.remove();
    this._toolbar = null;
  }

  /* ============================================================
   * Block settings (cog menu): vertical align + line-height
   * ============================================================ */

  renderSettings() {
    const panel = document.createElement("div");
    panel.className = "ce-textbox-settings";

    const label = (text) => {
      const l = document.createElement("div");
      l.className = "ce-textbox-settings__label";
      const s = document.createElement("span");
      s.textContent = text;
      l.appendChild(s);
      return l;
    };

    // Vertical alignment of the contents
    const vRow = document.createElement("div");
    vRow.className = "ce-textbox-settings__row";
    const vButtons = {};
    [
      ["top", "Align content to top", ICONS.vTop],
      ["middle", "Align content to middle", ICONS.vMiddle],
      ["bottom", "Align content to bottom", ICONS.vBottom],
    ].forEach(([key, title, icon]) => {
      const b = document.createElement("button");
      b.type = "button";
      b.className = "ce-textbox-settings__btn";
      b.title = title;
      b.setAttribute("aria-label", title);
      b.innerHTML = icon;
      b.classList.toggle("is-active", this._data.verticalAlign === key);
      b.addEventListener("click", () => {
        this._setVerticalAlign(key);
        Object.entries(vButtons).forEach(([k, btn]) =>
          btn.classList.toggle("is-active", k === key),
        );
      });
      vButtons[key] = b;
      vRow.appendChild(b);
    });

    // Line-height
    const lhLabel = label("Line height");
    const out = document.createElement("output");
    out.textContent = this._data.lineHeight.toFixed(2);
    lhLabel.appendChild(out);

    const range = document.createElement("input");
    range.type = "range";
    range.min = "0.8";
    range.max = "3";
    range.step = "0.05";
    range.value = String(this._data.lineHeight);
    range.setAttribute("aria-label", "Line height");
    range.addEventListener("input", () => {
      const v = parseFloat(range.value);
      out.textContent = v.toFixed(2);
      this._setLineHeight(v);
    });

    panel.append(label("Vertical alignment"), vRow, lhLabel, range);
    return panel;
  }

  _setVerticalAlign(key) {
    this._data.verticalAlign = key;
    this._applyLayout();
    this.block?.dispatchChange?.();
  }

  _setLineHeight(value) {
    this._data.lineHeight = value;
    this._applyLayout();
    this.block?.dispatchChange?.();
  }

  /* ============================================================
   * Floating toolbar
   * ============================================================ */

  _buildToolbar() {
    const bar = document.createElement("div");
    bar.className = "ce-textbox-toolbar";
    bar.hidden = true;
    bar.setAttribute("role", "toolbar");
    bar.setAttribute("aria-label", "Text box formatting");
    // The toolbar now lives inside the block, so keep Editor.js from treating
    // toolbar changes as content edits, and keys typed in it as block navigation.
    bar.dataset.mutationFree = "true";
    bar.addEventListener("keydown", (e) => e.stopPropagation());

    // Keep the text selection alive when clicking buttons.
    // (select / color input need real focus, so they're excluded — we restore the saved range instead.)
    bar.addEventListener("mousedown", (e) => {
      if (!e.target.closest("select, input")) e.preventDefault();
    });

    const group = (...els) => {
      const g = document.createElement("div");
      g.className = "ce-textbox-toolbar__group";
      g.append(...els);
      return g;
    };

    // Font family (primary / secondary, applied as a class)
    const family = document.createElement("select");
    family.className = "ce-textbox-toolbar__select";
    family.title = "Font family";
    family.setAttribute("aria-label", "Font family");
    FONT_FAMILIES.forEach(([key, name]) => {
      const o = document.createElement("option");
      o.value = key;
      o.textContent = name;
      family.appendChild(o);
    });
    family.addEventListener("change", () => this._setFontFamily(family.value));
    this._familySelect = family;

    // Font weight
    const weight = document.createElement("select");
    weight.className = "ce-textbox-toolbar__select";
    weight.title = "Font weight";
    weight.setAttribute("aria-label", "Font weight");
    WEIGHTS.forEach(([value, name]) => {
      const o = document.createElement("option");
      o.value = String(value);
      o.textContent = name;
      weight.appendChild(o);
    });
    weight.addEventListener("change", () =>
      this._setWeight(Number(weight.value)),
    );
    this._weightSelect = weight;

    // Font size
    const size = document.createElement("select");
    size.className =
      "ce-textbox-toolbar__select ce-textbox-toolbar__select--size";
    size.title = "Font size";
    size.setAttribute("aria-label", "Font size");
    FONT_SIZES.forEach((s) => {
      const o = document.createElement("option");
      o.value = String(s);
      o.textContent = String(s);
      size.appendChild(o);
    });
    size.addEventListener("change", () => this._setSize(Number(size.value)));
    this._sizeSelect = size;

    // Text colour
    // (Pickr is attached to this button in _initPickr, once the toolbar is in the DOM)
    const colorBtn = document.createElement("button");
    colorBtn.type = "button";
    colorBtn.className = "ce-textbox-toolbar__btn ce-textbox-toolbar__color";
    colorBtn.title = "Text color";
    colorBtn.setAttribute("aria-label", "Text color");
    colorBtn.innerHTML = `<span class="ce-textbox-toolbar__glyph" style="font-weight:700">A</span><span class="ce-textbox-toolbar__color-bar"></span>`;
    this._colorBtn = colorBtn;
    this._colorBar = colorBtn.querySelector(".ce-textbox-toolbar__color-bar");
    this._colorBar.style.background = "#000000";

    const cmd = (key, title, icon, command, css = false) =>
      this._button(key, title, icon, () => this._exec(command, null, css));

    bar.append(
      group(family, weight),
      group(
        size,
        this._button("sizeDown", "Decrease font size", ICONS.sizeDown, () =>
          this._stepSize(-1),
        ),
        this._button("sizeUp", "Increase font size", ICONS.sizeUp, () =>
          this._stepSize(1),
        ),
      ),
      group(
        cmd("bold", "Bold", ICONS.bold, "bold"),
        cmd("italic", "Italic", ICONS.italic, "italic"),
        cmd("underline", "Underline", ICONS.underline, "underline"),
        colorBtn,
      ),
      group(
        cmd("sub", "Subscript", ICONS.sub, "subscript"),
        cmd("sup", "Superscript", ICONS.sup, "superscript"),
      ),
      group(
        cmd("left", "Align left", ICONS.left, "justifyLeft", true),
        cmd("center", "Align center", ICONS.center, "justifyCenter", true),
        cmd("right", "Align right", ICONS.right, "justifyRight", true),
        cmd("justify", "Justify", ICONS.justify, "justifyFull", true),
      ),
      group(
        this._button("ul", "Bulleted list", ICONS.ul, () =>
          this._toggleList("UL"),
        ),
        this._button("ol", "Numbered list", ICONS.ol, () =>
          this._toggleList("OL"),
        ),
      ),
    );

    return bar;
  }

  _button(key, title, html, handler) {
    const b = document.createElement("button");
    b.type = "button";
    b.className = "ce-textbox-toolbar__btn";
    b.title = title;
    b.setAttribute("aria-label", title);
    b.setAttribute("aria-pressed", "false");
    b.innerHTML = html;
    b.addEventListener("click", () => {
      this._restoreSelection();
      handler();
      this._saveSelection();
      this._refreshState();
    });
    this._buttons[key] = b;
    return b;
  }

  _initPickr() {
    this._pickr = Pickr.create({
      el: this._colorBtn,
      useAsButton: true, // keep our own button + color bar instead of Pickr's
      theme: "nano", // "classic" | "monolith" | "nano"
      default: "#000000",
      defaultRepresentation: "HEXA",
      swatches: [
        "#000000",
        "#ffffff",
        "#e53935",
        "#fb8c00",
        "#fdd835",
        "#43a047",
        "#1e88e5",
        "#8e24aa",
      ],
      components: {
        preview: true,
        opacity: false, // foreColor doesn't use alpha
        hue: true,
        interaction: { hex: true, rgba: false, input: true, save: true },
      },
      i18n: { "btn:save": "Apply" },
    });

    // The click on the toolbar button doesn't move focus (mousedown is prevented),
    // so the editor selection is still live here; remember it for when we apply.
    this._pickr.on("show", () => this._saveSelection());

    this._pickr.on("save", (color) => {
      if (!color) return;
      const hex = color.toHEXA().toString().slice(0, 7); // "#RRGGBB" (drop any alpha)
      this._restoreSelection();
      this._exec("foreColor", hex, true);
      this._saveSelection();
      this._refreshState();
      this._pickr.hide();
    });
  }

  _showToolbar() {
    if (this.readOnly || this._toolbarOpen) return;
    if (!this._toolbar) this._toolbar = this._buildToolbar();
    if (!this._toolbar.isConnected) this._element.appendChild(this._toolbar);
    if (!this._pickr) this._initPickr();

    this._toolbarOpen = true;
    this._toolbar.hidden = false;
    this._placeToolbar();

    document.addEventListener("selectionchange", this._onSelectionChange);
    document.addEventListener("pointerdown", this._onDocPointerDown, true);
    document.addEventListener("focusin", this._onDocFocusIn, true);

    this._saveSelection();
    this._refreshState();
  }

  _hideToolbar() {
    if (!this._toolbarOpen) return;
    this._toolbarOpen = false;
    document.removeEventListener("selectionchange", this._onSelectionChange);
    document.removeEventListener("pointerdown", this._onDocPointerDown, true);
    document.removeEventListener("focusin", this._onDocFocusIn, true);
    if (this._pickr) this._pickr.hide();
    if (this._toolbar) this._toolbar.hidden = true;
  }

  /**
   * The toolbar is absolutely positioned inside the block (above it by default),
   * so it follows the block on its own. Only flip it below when there's no room above.
   */
  _placeToolbar() {
    const bar = this._toolbar;
    bar.classList.remove("ce-textbox-toolbar--below");
    const need = bar.offsetHeight + 8;
    if (this._element.getBoundingClientRect().top < need) {
      bar.classList.add("ce-textbox-toolbar--below");
    }
  }

  /** True for the block, our toolbar, and Editor.js's own toolbar / settings popover. */
  _isOwnUi(target) {
    if (!(target instanceof Element)) return false;
    return (
      this._element.contains(target) ||
      (this._toolbar && this._toolbar.contains(target)) ||
      !!target.closest(".ce-toolbar, .ce-popover, .ce-settings, .pcr-app")
    );
  }

  _onDocPointerDown(e) {
    if (!this._isOwnUi(e.target)) this._hideToolbar();
  }

  _onDocFocusIn(e) {
    if (!this._isOwnUi(e.target)) this._hideToolbar();
  }

  /* ============================================================
   * Selection helpers
   * ============================================================ */

  _selectionElement() {
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount) return null;
    let n = sel.anchorNode;
    if (n && n.nodeType === Node.TEXT_NODE) n = n.parentElement;
    return n && this._editable.contains(n) ? n : null;
  }

  /**
   * The element whose formatting actually applies to the selection.
   * For a range selection this is the parent of the first text node inside the range,
   * (anchorNode alone can be a container element, which reports the wrong font size).
   */
  _formatElement() {
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount) return null;
    const range = sel.getRangeAt(0);
    const root = range.commonAncestorContainer;
    if (!this._editable.contains(root)) return null;

    if (!range.collapsed) {
      if (root.nodeType === Node.TEXT_NODE) return root.parentElement;
      const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
      let n;
      while ((n = walker.nextNode())) {
        if (n.textContent.trim() !== "" && range.intersectsNode(n)) {
          return n.parentElement;
        }
      }
    }
    return this._selectionElement();
  }

  /** Select the text inside the given spans so the anchor/focus nodes are text nodes, not containers. */
  _selectSpans(spans) {
    const sel = window.getSelection();
    const firstText = (el) => {
      const w = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
      return w.nextNode();
    };
    const lastText = (el) => {
      const w = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
      let last = null;
      let n;
      while ((n = w.nextNode())) last = n;
      return last;
    };

    const start = firstText(spans[0]);
    const end = lastText(spans[spans.length - 1]);
    const range = document.createRange();
    if (start && end) {
      range.setStart(start, 0);
      range.setEnd(end, end.length);
    } else {
      range.setStartBefore(spans[0]);
      range.setEndAfter(spans[spans.length - 1]);
    }
    sel.removeAllRanges();
    sel.addRange(range);
  }

  _saveSelection() {
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount) return;
    const range = sel.getRangeAt(0);
    if (this._editable.contains(range.commonAncestorContainer)) {
      this._savedRange = range.cloneRange();
    }
  }

  _restoreSelection() {
    this._editable.focus({ preventScroll: true });
    if (!this._savedRange) return;
    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(this._savedRange);
  }

  _onSelectionChange() {
    if (!this._selectionElement()) return;
    this._saveSelection();
    this._refreshState();
  }

  /* ============================================================
   * Formatting commands
   * ============================================================ */

  /**
   * @param {string} command
   * @param {string|null} value
   * @param {boolean} css - true → inline style (needed for color / text-align), false → semantic tags (<b>, <i>, <u>, <ul>…)
   */
  _exec(command, value = null, css = false) {
    document.execCommand("styleWithCSS", false, css);
    document.execCommand(command, false, value);
    this._toggleEmpty();
  }

  _setFontFamily(key) {
    const entry = FONT_FAMILIES.find(([k]) => k === key);
    if (!entry) return;
    const [, , className] = entry;

    this._restoreSelection();
    const sel = window.getSelection();
    if (!sel.rangeCount || sel.isCollapsed) return;

    const MARKER = "ce-textbox-family-marker";
    document.execCommand("styleWithCSS", false, false);
    document.execCommand("fontName", false, MARKER);

    const spans = [];
    this._editable
      .querySelectorAll(`font[face="${MARKER}"]`)
      .forEach((font) => {
        const span = document.createElement("span");
        span.className = className;
        while (font.firstChild) span.appendChild(font.firstChild);

        // The new family wins over any nested family classes.
        span.querySelectorAll("[class]").forEach((n) => {
          FONT_CLASSES.forEach((c) => n.classList.remove(c));
          if (!n.getAttribute("class")) n.removeAttribute("class");
        });

        font.replaceWith(span);
        spans.push(span);
      });

    if (spans.length) this._selectSpans(spans);

    this._saveSelection();
    this._refreshState();
    this._toggleEmpty();
    this.block?.dispatchChange?.();
  }

  /**
   * Font weight has no execCommand, so we use the fontSize trick:
   * mark the selection with <font size=7>, then swap those for <span style="font-weight:N">.
   */
  _setWeight(weight) {
    this._restoreSelection();
    const sel = window.getSelection();
    if (!sel.rangeCount || sel.isCollapsed) return;

    // Marker via fontName instead of fontSize: fontSize makes the browser strip
    // existing inline font-size styles in the range, which reset the size.
    const MARKER = "ce-textbox-weight-marker";
    document.execCommand("styleWithCSS", false, false);
    document.execCommand("fontName", false, MARKER);

    const spans = [];
    this._editable
      .querySelectorAll(`font[face="${MARKER}"]`)
      .forEach((font) => {
        const span = document.createElement("span");
        span.style.fontWeight = String(weight);
        while (font.firstChild) span.appendChild(font.firstChild);
        // The new weight wins over any nested weight / bold (font-size is left alone).
        span
          .querySelectorAll("[style]")
          .forEach((n) => n.style.removeProperty("font-weight"));
        span.querySelectorAll("b, strong").forEach(unwrap);
        font.replaceWith(span);
        spans.push(span);
      });

    if (spans.length) this._selectSpans(spans);

    this._saveSelection();
    this._refreshState();
    this._toggleEmpty();
  }

  /** Size (px) of the current selection. */
  _currentSize() {
    const el = this._formatElement();
    return el
      ? Math.round(parseFloat(getComputedStyle(el).fontSize)) || 16
      : 16;
  }

  /** Step to the next larger / smaller preset; outside the presets, move by 2px. */
  _stepSize(dir) {
    const cur = this._currentSize();
    let next;
    if (dir > 0) {
      next = FONT_SIZES.find((s) => s > cur) ?? cur + 2;
    } else {
      next = [...FONT_SIZES].reverse().find((s) => s < cur) ?? cur - 2;
    }
    this._setSize(next);
  }

  /** Same fontSize trick as _setWeight, but writes font-size in px. */
  _setSize(px) {
    px = Math.min(MAX_SIZE, Math.max(MIN_SIZE, Math.round(px)));
    this._restoreSelection();
    const sel = window.getSelection();
    if (!sel.rangeCount || sel.isCollapsed) return;

    document.execCommand("styleWithCSS", false, false);
    document.execCommand("fontSize", false, "7");

    const spans = [];
    this._editable.querySelectorAll('font[size="7"]').forEach((font) => {
      const span = document.createElement("span");
      span.style.fontSize = `${px}px`;
      while (font.firstChild) span.appendChild(font.firstChild);
      // The new size wins over any nested sizes.
      span
        .querySelectorAll("[style]")
        .forEach((n) => n.style.removeProperty("font-size"));
      font.replaceWith(span);
      spans.push(span);
    });

    if (spans.length) this._selectSpans(spans);

    this._saveSelection();
    this._refreshState();
    this._toggleEmpty();
  }

  /** Reflect the current selection in the toolbar. */
  _refreshState() {
    if (!this._toolbar || !this._selectionElement()) return;

    const state = (c) => {
      try {
        return document.queryCommandState(c);
      } catch (e) {
        return false;
      }
    };
    const map = {
      bold: "bold",
      italic: "italic",
      underline: "underline",
      sub: "subscript",
      sup: "superscript",
      left: "justifyLeft",
      center: "justifyCenter",
      right: "justifyRight",
      justify: "justifyFull",
      ul: "insertUnorderedList",
      ol: "insertOrderedList",
    };
    Object.entries(map).forEach(([key, command]) => {
      this._buttons[key]?.setAttribute("aria-pressed", String(state(command)));
    });

    // colour
    let hex = null;
    try {
      hex = rgbToHex(document.queryCommandValue("foreColor"));
    } catch (e) {
      /* ignore */
    }
    if (hex) {
      this._colorBar.style.background = hex;
      // second arg = silent: update the picker without firing "save"/"change"
      if (this._pickr) this._pickr.setColor(hex, true);
    }

    // weight → nearest option
    const el = this._formatElement() || this._selectionElement();
    const current = parseInt(getComputedStyle(el).fontWeight, 10) || 400;
    const nearest = WEIGHTS.reduce(
      (best, [w]) =>
        Math.abs(w - current) < Math.abs(best - current) ? w : best,
      WEIGHTS[0][0],
    );
    this._weightSelect.value = String(nearest);

    // size → select (add a custom option if it's not a preset)
    const size = this._currentSize();
    let opt = Array.from(this._sizeSelect.options).find(
      (o) => o.value === String(size),
    );
    if (!opt) {
      opt = document.createElement("option");
      opt.value = String(size);
      opt.textContent = String(size);
      opt.dataset.custom = "1";
      // insert in numeric order
      const after = Array.from(this._sizeSelect.options).find(
        (o) => Number(o.value) > size,
      );
      this._sizeSelect.insertBefore(opt, after || null);
    }
    this._sizeSelect.value = String(size);
    // drop stale custom options
    Array.from(this._sizeSelect.options).forEach((o) => {
      if (o.dataset.custom && o.value !== String(size)) o.remove();
    });

    // font family → nearest ancestor carrying one of our classes (default: primary)
    let familyKey = FONT_FAMILIES[0][0];
    for (let n = el; n && n !== this._editable; n = n.parentElement) {
      const hit = FONT_FAMILIES.find(([, , cls]) => n.classList.contains(cls));
      if (hit) {
        familyKey = hit[0];
        break;
      }
    }
    this._familySelect.value = familyKey;
  }

  /* ============================================================
   * Keyboard + paste
   * ============================================================ */

  /**
   * Lists are handled entirely by us (Tab, Enter on an empty item, Backspace at the
   * start of an item). The browser's native versions of these produce stray
   * <p><br></p> blocks inside <li>, which is what made the bullets flaky.
   */
  _onKeyDown(e) {
    if (e.ctrlKey || e.metaKey || e.altKey) return;

    // Tab / Shift+Tab change the nesting level of the selected list items
    if (e.key === "Tab") {
      const items = this._selectedItems();
      if (!items.length) return; // not in a list: let Editor.js handle Tab

      e.preventDefault();
      e.stopPropagation(); // keep Editor.js from treating Tab as block navigation
      this._preserveSelection(() =>
        e.shiftKey ? this._outdentItems(items) : this._indentItems(items),
      );
      this._afterListChange();
      return;
    }

    if (e.shiftKey || (e.key !== "Enter" && e.key !== "Backspace")) return;

    const sel = window.getSelection();
    if (!sel || !sel.rangeCount || !sel.isCollapsed) return;

    const li = this._caretItem();
    if (!li) return;

    // Enter on an empty item  → move it out one level (or back to a paragraph at the top level)
    // Backspace at item start → same thing
    // Anything else (Enter in the middle of text, ...) stays native: it splits <li> correctly.
    const leave =
      e.key === "Enter" ? this._itemIsBlank(li) : this._caretAtItemStart(li);
    if (!leave) return;

    e.preventDefault();
    e.stopPropagation(); // also keeps Editor.js from merging this block on Backspace
    this._preserveSelection(() => this._leaveItem(li));
    this._afterListChange();
  }

  /* ============================================================
   * Lists: nesting + type switching.
   * Done with plain DOM operations because execCommand's indent /
   * insertOrderedList produce invalid or inconsistent markup
   * (<ul><ul>, lists converting at the wrong level, ...).
   * ============================================================ */

  /** Repair markup that the browser (or pasted HTML) can leave behind. */
  _normalizeLists() {
    // Merge adjacent sibling lists of the same type (e.g., <ul>...</ul> followed by <ul>...</ul>)
    this._editable.querySelectorAll("ul + ul, ol + ol").forEach((list) => {
      const prev = list.previousElementSibling;
      if (prev && prev.tagName === list.tagName) {
        while (list.firstChild) {
          prev.appendChild(list.firstChild);
        }
        list.remove();
      }
    });

    // <ul><ul>…</ul></ul> → move the inner list into the previous <li>
    this._editable
      .querySelectorAll("ul > ul, ul > ol, ol > ul, ol > ol")
      .forEach((child) => {
        const prev = child.previousElementSibling;
        if (prev && prev.tagName === "LI") {
          prev.appendChild(child);
        } else {
          const li = document.createElement("li");
          child.replaceWith(li);
          li.appendChild(child);
        }
      });

    // <li>…<p><br></p></li> → blocks inside list items are never wanted
    this._editable.querySelectorAll("li > p, li > div").forEach((blk) => {
      const li = blk.parentElement;
      const blank = blk.textContent.trim() === "";
      const others = Array.from(li.childNodes).filter(
        (n) =>
          n !== blk &&
          !(n.nodeType === Node.TEXT_NODE && !n.textContent.trim()),
      );

      if (blank && others.length) {
        blk.remove();
        return;
      }
      const prev = blk.previousSibling;
      if (!blank && prev && !isList(prev))
        blk.before(document.createElement("br"));
      unwrap(blk);
    });
  }

  /** The innermost <li> that holds the caret. */
  _caretItem() {
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount) return null;
    let n = sel.getRangeAt(0).startContainer;
    if (n.nodeType === Node.TEXT_NODE) n = n.parentElement;
    const li = n && n.closest ? n.closest("li") : null;
    return li && this._editable.contains(li) ? li : null;
  }

  /** True when the item has no text of its own (sub-lists don't count). */
  _itemIsBlank(li) {
    return Array.from(li.childNodes).every(
      (n) => isList(n) || n.textContent.trim() === "",
    );
  }

  _caretAtItemStart(li) {
    const range = window.getSelection().getRangeAt(0);
    const before = document.createRange();
    before.setStart(li, 0);
    before.setEnd(range.startContainer, range.startOffset);
    return before.toString() === "";
  }

  /** Nested item → one level up. Top-level item → paragraph. */
  _leaveItem(li) {
    const parentLi = li.parentElement && li.parentElement.parentElement;
    if (parentLi && parentLi.tagName === "LI") {
      this._outdentItems([li]);
      return li;
    }
    return this._unlistItems([li]);
  }

  /** Keep outdenting until the item is in a top-level list. */
  _outdentFully(li) {
    for (let guard = 0; guard < 20; guard++) {
      const parentLi = li.parentElement && li.parentElement.parentElement;
      if (!parentLi || parentLi.tagName !== "LI") return;
      this._outdentItems([li]);
    }
  }

  /** The <li>s whose own text is touched by the selection (nested lists' items are separate). */
  _selectedItems() {
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount) return [];
    const range = sel.getRangeAt(0);
    if (!this._editable.contains(range.commonAncestorContainer)) return [];

    if (range.collapsed) {
      let n = range.startContainer;
      if (n.nodeType === Node.TEXT_NODE) n = n.parentElement;
      const li = n && n.closest ? n.closest("li") : null;
      return li && this._editable.contains(li) ? [li] : [];
    }

    return Array.from(this._editable.querySelectorAll("li")).filter((li) => {
      const own = document.createRange();
      own.setStart(li, 0);
      const nested = Array.from(li.children).find(isList);
      if (nested) own.setEndBefore(nested);
      else own.setEnd(li, li.childNodes.length);
      // strict overlap, so a selection that merely touches an item's edge doesn't count
      return (
        range.compareBoundaryPoints(Range.START_TO_END, own) > 0 &&
        range.compareBoundaryPoints(Range.END_TO_START, own) < 0
      );
    });
  }

  /** Drop items that are inside another selected item; they move along with it. */
  _topItems(items) {
    return items.filter((li) => !items.some((o) => o !== li && o.contains(li)));
  }

  /** Put an item (and its sub-items) into the sub-list of its previous sibling. */
  _indentItems(items) {
    this._topItems(items).forEach((li) => {
      const prev = li.previousElementSibling;
      if (!prev || prev.tagName !== "LI") return; // first item can't be indented
      const list = li.parentElement;
      let sub = prev.lastElementChild;
      if (!sub || sub.tagName !== list.tagName) {
        sub = document.createElement(list.tagName.toLowerCase());
        prev.appendChild(sub);
      }
      sub.appendChild(li);
    });
  }

  /** Move an item up one level, right after its parent item. Items below it become its children. */
  _outdentItems(items) {
    this._topItems(items).forEach((li) => {
      const list = li.parentElement;
      const parentLi = list.parentElement;
      if (!parentLi || parentLi.tagName !== "LI") return; // already top level

      const following = [];
      for (let n = li.nextElementSibling; n; n = n.nextElementSibling)
        following.push(n);
      if (following.length) {
        let sub = li.lastElementChild;
        if (!sub || sub.tagName !== list.tagName) {
          sub = document.createElement(list.tagName.toLowerCase());
          li.appendChild(sub);
        }
        following.forEach((n) => sub.appendChild(n));
      }

      parentLi.after(li);
      if (!list.children.length) list.remove();
    });
  }
  _blocksInSelection() {
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount) return [];
    const range = sel.getRangeAt(0);
    if (!this._editable.contains(range.commonAncestorContainer)) return [];
    return Array.from(this._editable.children).filter((c) => {
      if (!range.intersectsNode(c)) return false;
      // a selection that ends at the very start of a block shouldn't pull that block in
      if (!range.collapsed && c !== this._editable.children[0]) {
        const startOfC = document.createRange();
        startOfC.setStart(c, 0);
        startOfC.collapse(true);
        if (range.compareBoundaryPoints(Range.END_TO_START, startOfC) === 0)
          return false;
      }
      return true;
    });
  }
  /**
   * Bulleted / numbered buttons.
   * - not in a list        → native command (creates a top-level list)
   * - different list type  → switch the list at that level, keeping its nesting
   * - same list type       → remove the bullet (nested items are first moved to the top level)
   */
  _toggleList(tag) {
    const items = this._selectedItems();
    if (!items.length) {
      const blocks = this._blocksInSelection().filter((b) => !isList(b));

      if (blocks.length) {
        const first = blocks[0];
        const last = blocks[blocks.length - 1];
        const prev = first.previousElementSibling;
        const next = last.nextElementSibling;
        const target =
          prev && prev.tagName === tag
            ? prev
            : next && next.tagName === tag
              ? next
              : null;

        if (target) {
          this._preserveSelection(() => {
            const lis = blocks.map((b) => {
              const li = document.createElement("li");
              while (b.firstChild) li.appendChild(b.firstChild);
              return li;
            });
            if (target === prev) {
              lis.forEach((li) => target.appendChild(li));
            } else {
              // insert at the front, in reverse, to keep the original order
              [...lis]
                .reverse()
                .forEach((li) => target.insertBefore(li, target.firstChild));
            }
            blocks.forEach((b) => b.remove());
            return lis[0];
          });
          this._afterListChange();
          return;
        }
      }

      this._exec(tag === "UL" ? "insertUnorderedList" : "insertOrderedList");
      this._afterListChange();
      return;
    }

    const lists = [...new Set(items.map((li) => li.parentElement))];
    this._preserveSelection(() => {
      if (lists.some((l) => l.tagName !== tag)) {
        lists.forEach((l) => {
          if (l.tagName !== tag) this._retag(l, tag);
        });
        return null;
      }
      items.forEach((li) => this._outdentFully(li));
      return this._unlistItems(items);
    });
    this._afterListChange();
  }

  /** Swap a <ul> for an <ol> (or back), keeping its items (and their nested lists). */
  _retag(list, tag) {
    const next = document.createElement(tag.toLowerCase());
    while (list.firstChild) next.appendChild(list.firstChild);
    list.replaceWith(next);
  }

  /** Turn top-level list items into paragraphs, splitting the list around them. */
  _unlistItems(items) {
    let last = null;
    items.forEach((li) => {
      const list = li.parentElement;
      const p = document.createElement("p");
      const nested = [];
      Array.from(li.childNodes).forEach((n) => {
        if (isList(n)) nested.push(n);
        else p.appendChild(n);
      });
      if (!p.textContent && !p.querySelector("br"))
        p.appendChild(document.createElement("br"));

      const tail = [];
      for (let n = li.nextElementSibling; n; n = n.nextElementSibling)
        tail.push(n);
      if (tail.length) {
        const rest = document.createElement(list.tagName.toLowerCase());
        tail.forEach((n) => rest.appendChild(n));
        list.after(rest);
      }
      list.after(p, ...nested); // lands before `rest`, right after the list

      li.remove();
      if (!list.children.length) list.remove();
      last = p;
    });
    return last; // where to put the caret if the original selection node is gone
  }

  /** Run a DOM change and put the selection back (moving nodes collapses ranges inside them). */
  _preserveSelection(fn) {
    const sel = window.getSelection();
    const r = sel && sel.rangeCount ? sel.getRangeAt(0) : null;
    const pts = r && {
      sc: r.startContainer,
      so: r.startOffset,
      ec: r.endContainer,
      eo: r.endOffset,
    };

    const fallback = fn();
    if (!sel) return;

    const range = document.createRange();
    try {
      if (
        pts &&
        this._editable.contains(pts.sc) &&
        this._editable.contains(pts.ec)
      ) {
        range.setStart(pts.sc, pts.so);
        range.setEnd(pts.ec, pts.eo);
      } else if (fallback) {
        range.selectNodeContents(fallback);
        range.collapse(true);
      } else {
        return;
      }
    } catch (e) {
      return;
    }
    sel.removeAllRanges();
    sel.addRange(range);
  }

  _afterListChange() {
    this._normalizeLists();
    this._saveSelection();
    this._refreshState();
    this._toggleEmpty();
  }

  /**
   * Keep pasted content inside this block (Editor.js would otherwise split
   * multi-paragraph pastes into separate blocks) and strip unsupported markup.
   */
  _onPaste(e) {
    e.preventDefault();
    e.stopPropagation();
    const cd = e.clipboardData;
    if (!cd) return;
    const html = cd.getData("text/html");
    const text = cd.getData("text/plain");
    if (html) {
      document.execCommand("insertHTML", false, cleanHtml(html));
    } else if (text) {
      document.execCommand("insertText", false, text);
    }
    this._normalizeLists();
    this._toggleEmpty();
  }
}
